import { SAlert, SDialog } from "@avalara/skylab-sdk/react";
import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { testIDs } from "../../../end-to-end-tests/constants/testIDs";

interface ImportOverwriteConfirmDialogProps {
    open: boolean;
    /** The active company's name (from the session), shown in the header company switcher. */
    companyName: string;
    companyCount: number;
    isSubmitting: boolean;
    onBack: () => void;
    onConfirm: () => void;
}

const DIALOG_ID = "dataTransferConfirmDialog";

/**
 * Final gate before an irreversible overwrite: the user must retype the active company name to
 * confirm. When the session carries no active company name there is nothing to type against, so
 * the gate shows an explicit error rather than leaving the confirm button permanently (and
 * silently) disabled.
 */
const ImportOverwriteConfirmDialog: React.FC<ImportOverwriteConfirmDialogProps> = ({
    open,
    companyName,
    companyCount,
    isSubmitting,
    onBack,
    onConfirm,
}) => {
    const [typed, setTyped] = useState("");

    useEffect(() => {
        if (!open) setTyped("");
    }, [open]);

    const hasCompanyName = companyName.length > 0;
    const matches = hasCompanyName && typed.trim() === companyName;

    return (
        <SDialog
            id={DIALOG_ID}
            open={open}
            data-testid={testIDs.dataTransfer.confirmDialog}
            onS-dismiss={(e: CustomEvent<{ id?: string }>) => {
                if (e.detail?.id === DIALOG_ID) onBack();
            }}
            aria-modal="true"
            role="alertdialog">
            <div slot="header">Confirm data overwrite</div>
            <div slot="body">
                <SAlert status="error" noDismiss className="margin-bottom-md">
                    This replaces existing data for {companyCount}{" "}
                    {companyCount === 1 ? "mapped company" : "mapped companies"}. Changes may also
                    update AvaTax and other downstream Avalara products connected to this account.
                    This cannot be undone.
                </SAlert>
                {hasCompanyName ? (
                    <React.Fragment>
                        <label htmlFor="data-transfer-confirm-name">
                            Type your active company name, <strong>{companyName}</strong>, to
                            confirm
                        </label>
                        <input
                            id="data-transfer-confirm-name"
                            type="text"
                            autoComplete="off"
                            value={typed}
                            data-testid={testIDs.dataTransfer.confirmInput}
                            onChange={e => setTyped(e.target.value)}
                        />
                        <div className="data-transfer-count">
                            Enter it exactly as shown in the company switcher.
                        </div>
                    </React.Fragment>
                ) : (
                    <SAlert
                        status="error"
                        noDismiss
                        data-testid={testIDs.dataTransfer.confirmNoName}>
                        We couldn&apos;t determine your active company, so this import can&apos;t be
                        confirmed. Reload the page and try again.
                    </SAlert>
                )}
            </div>
            <div slot="footer">
                <button
                    type="button"
                    className="secondary"
                    disabled={isSubmitting}
                    onClick={onBack}>
                    Back
                </button>
                <button
                    type="button"
                    className={classNames({ primary: true, loading: isSubmitting })}
                    disabled={!matches || isSubmitting}
                    data-testid={testIDs.dataTransfer.confirmImport}
                    onClick={onConfirm}>
                    Import and overwrite
                </button>
            </div>
        </SDialog>
    );
};

export default ImportOverwriteConfirmDialog;
